import { useMutation, useQuery } from '@apollo/client';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import Table from '@risksmart-app/components/src/table';
import {
  DeleteOversightResultsDocument,
  GetAllOversightAssessmentResultsDocument,
  GetOversightAssessmentDocumentAssessmentResultsByDocumentIdDocument,
  GetOversightAssessmentObligationAssessmentResultsByObligationIdDocument,
  GetOversightAssessmentRiskAssessmentResultsByRiskIdDocument,
  GetOversightAssessmentTestResultsByControlIdDocument,
  GetOversightResultsByParentIdDocument,
  Parent_Type_Enum,
  Risk_Assessment_Result_Control_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import CustomisableRibbon from 'src/components/customisable-ribbon/CustomisableRibbon';
import DeleteModal from 'src/components/delete-modal';
import ExportButton from 'src/components/export-button';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { handleError } from '@/utils/errorUtils';
import { evictField } from '@/utils/graphqlUtils';

import { useGetCollectionTableProps } from './config';
import type { OversightAssessmentResultRegisterFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const OversightAssessmentResultsPage: FC = () => {
  const { t: st } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const { t } = useTranslation('common');

  const { addNotification } = useNotifications();
  const { data, loading } = useQuery(GetAllOversightAssessmentResultsDocument, {
    onError: (error) => {
      handleError(error);
      addNotification({
        type: 'error',
        content: <>{error.message}</>,
      });
    },
  });
  const [selectedOversightResults, setSelectedOversightResults] = useState<
    OversightAssessmentResultRegisterFields[]
  >([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const {
    hasPermission: canDeleteDocumentOversightResult,
    loading: canDeleteDocumentOversightResultLoading,
  } = useHasPermissionQuery('delete:document_second_line_result');
  const {
    hasPermission: canDeleteObligationOversightResult,
    loading: canDeleteObligationOversightResultLoading,
  } = useHasPermissionQuery('delete:obligation_second_line_result');
  const {
    hasPermission: canDeleteRiskControlledOversightResult,
    loading: canDeleteRiskControlledOversightResultLoading,
  } = useHasPermissionQuery('delete:risk_controlled_second_line_result');
  const {
    hasPermission: canDeleteRiskUncontrolledOversightResult,
    loading: canDeleteRiskUncontrolledOversightResultLoading,
  } = useHasPermissionQuery('delete:risk_uncontrolled_second_line_result');
  const isLoading =
    canDeleteDocumentOversightResultLoading ||
    canDeleteObligationOversightResultLoading ||
    canDeleteRiskControlledOversightResultLoading ||
    canDeleteRiskUncontrolledOversightResultLoading;
  const canDeleteOversightResult =
    !isLoading &&
    (canDeleteDocumentOversightResult ||
      canDeleteObligationOversightResult ||
      canDeleteRiskControlledOversightResult ||
      canDeleteRiskUncontrolledOversightResult);

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await deleteOversightResults({
        variables: {
          Ids: selectedOversightResults?.map((s) => s.originalResult.Id),
        },
      });
      setSelectedOversightResults([]);
      setIsDeleteModalVisible(false);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const [deleteOversightResults, deleteResult] = useMutation(
    DeleteOversightResultsDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_oversight_result');
        evictField(cache, 'document_oversight_result');
        evictField(cache, 'control_test_oversight_result');
        evictField(cache, 'risk_controlled_oversight_result');
        evictField(cache, 'risk_uncontrolled_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'obligation_oversight_result_aggregate');
        evictField(cache, 'document_oversight_result_aggregate');
        evictField(cache, 'control_test_oversight_result_aggregate');
        evictField(cache, 'risk_controlled_oversight_result_aggregate');
        evictField(cache, 'risk_uncontrolled_oversight_result_aggregate');
      },
      refetchQueries: [
        GetOversightResultsByParentIdDocument,
        GetAllOversightAssessmentResultsDocument,
        GetOversightAssessmentDocumentAssessmentResultsByDocumentIdDocument,
        GetOversightAssessmentObligationAssessmentResultsByObligationIdDocument,
        GetOversightAssessmentRiskAssessmentResultsByRiskIdDocument,
        GetOversightAssessmentTestResultsByControlIdDocument,
      ],
    }
  );

  const navigate = useNavigate();

  const handleOpenRating = (id: string) => {
    navigate(id);
  };

  const tableProps = useGetCollectionTableProps(
    [
      ...(data?.document_oversight_result || []),
      ...(data?.obligation_oversight_result || []),
      ...(data?.control_test_oversight_result || []),
      ...(data?.risk_controlled_oversight_result?.map((c) => ({
        ...c,
        ControlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
      })) || []),
      ...(data?.risk_uncontrolled_oversight_result?.map((c) => ({
        ...c,
        ControlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
      })) || []),
    ],
    handleOpenRating
  );

  const assessmentResultCount = useMemo(() => {
    if (loading) {
      return '';
    }

    return `(${tableProps.allItems?.length})`;
  }, [loading, tableProps]);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const title = t('navigationMenu.assessments.oversightFindingsTitle');

  return (
    <PageLayout
      helpTranslationKey={'assessmentResults.registerHelp'}
      title={title}
      counter={assessmentResultCount}
      actions={
        <SpaceBetween direction={'horizontal'} size={'xxs'}>
          {canDeleteOversightResult && (
            <Button
              formAction={'none'}
              variant={'normal'}
              disabled={!selectedOversightResults.length}
              onClick={() => setIsDeleteModalVisible(true)}
            >
              {st('delete_button')}
            </Button>
          )}
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.ComplianceMonitoringAssessmentResult}
        {...ribbonProps}
      />
      <Table
        {...tableProps}
        loading={loading}
        selectionType={canDeleteOversightResult ? 'multi' : undefined}
        selectedItems={selectedOversightResults}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedOversightResults(detail.selectedItems);
        }}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        loading={deleteResult.loading}
        header={st('delete_button')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default OversightAssessmentResultsPage;
